import { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { authService } from '../../services/authService';

function EditPilote() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: ''
  });

  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    const fetchPilote = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/users/${id}`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!response.ok) {
          throw new Error("Ce pilote n'existe pas");
        }

        const data = await response.json();
        setFormData(data.user);
      } catch (err) {
        console.error("Erreur lors du chargement du pilote:", err);
        setError(err.message || "Impossible de charger les données du pilote");
      } finally {
        setLoading(false);
      }
    };

    fetchPilote();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/users/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la modification du pilote');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/pilotes');
      }, 2000);
    } catch (err) {
      console.error("Erreur lors de la modification du pilote:", err);
      setError(err.message || "Une erreur est survenue lors de la modification");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression du pilote');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/pilotes');
      }, 2000);
    } catch (err) {
      console.error("Erreur lors de la suppression du pilote:", err);
      setError(err.message || "Une erreur est survenue lors de la suppression");
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/pilotes')}
        variant="contained"
        color="primary"
        sx={{ mb: 4 }}
      >
        Retour aux pilotes
      </Button>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Modifier le pilote
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Modifications enregistrées avec succès! Redirection...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            required
            fullWidth
            label="Nom"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Prénom"
            name="prenom"
            value={formData.prenom}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            sx={{ mb: 3 }}
            disabled
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setOpenDeleteDialog(true)}
              disabled={loading}
            >
              Supprimer le pilote
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/pilotes')}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? 'Modification...' : 'Modifier le pilote'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Dialog de confirmation de suppression */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => {
            setOpenDeleteDialog(false);
            setDeletePassword('');
            setDeleteError('');
          }}
        >
          <DialogTitle>
            Confirmer la suppression
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Êtes-vous sûr de vouloir supprimer ce pilote ? Cette action est irréversible.
            </DialogContentText>
            <TextField
              fullWidth
              label="Mot de passe du pilote"
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              error={!!deleteError}
              helperText={deleteError}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setOpenDeleteDialog(false);
                setDeletePassword('');
                setDeleteError('');
              }}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleDelete}
              color="error"
              disabled={loading || !deletePassword}
              variant="contained"
            >
              {loading ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
}

export default EditPilote; 